/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Unified file upload helper.
 * Always uploads to R2 via POST /api/media/upload, returns a URL.
 * Local dev uses wrangler dev which writes to the local .wrangler R2 store.
 */

import { getRepository } from '../config/repository';

/**
 * Returns a fully-qualified media URL with the auth token for R2-hosted images.
 * Useful for <img src="..." /> elements that cannot send Authorization headers.
 */
export function getMediaUrl(url?: string): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('http') || url.startsWith('data:')) return url; // Already fully qualified or data URI

  const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:8787';
  
  // Try to grab the token directly from local storage if available
  const lsToken = localStorage.getItem('dm_jwt');
  const tokenParams = lsToken ? `?token=${encodeURIComponent(lsToken)}` : '';
  
  return `${API_BASE}${url}${tokenParams}`;
}

/**
 * Upload a file and return a URL string usable in <img src=...>.
 * Always uploads to R2 (local wrangler dev or remote Cloudflare).
 */
export async function uploadFile(
  file: File,
  opts?: { moduleId?: string; entityId?: string },
): Promise<string> {
  const repo = getRepository() as any;
  const result = await repo.uploadMedia(file, opts?.moduleId, opts?.entityId);
  return result.url;
}
